import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import LocationManager from './LocationManager';
import CategoryManager from './CategoryManager';
import LogoUploader from "./LogoUploader";

const EditPage = ({ pageId }) => {
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingLanguages, setLoadingLanguages] = useState(true);
    const [loadingPage, setLoadingPage] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(null);
    const [title, setTitle] = useState('');
    const [webpage, setWebpage] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [logo, setLogo] = useState('');
    const [content, setContent] = useState({});
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [locations, setLocations] = useState([]);
    const [newLocations, setNewLocations] = useState([]);
    const [serviceInstances, setSelectedServices] = useState([]);
    const [editor, setEditor] = useState(null);
    const [categories, setCategories] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [csrfToken, setCsrfToken] = useState(null);
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        // Get CSRF token from body attribute
        const token = JSON.parse(document.body.getAttribute("hx-headers") || "{}")['X-CSRF-TOKEN'];
        setCsrfToken(token);
        
        fetchPageData(token);
        fetchCategories(token);
        fetchLanguages(token);
    }, [pageId]);


    const onBeforeUnload = (ev) => {
        if (hasChanges) {
            ev.returnValue = true;
            return true;
        } else {
            return false;
        }
    };

    useEffect(() => {
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [hasChanges]);

    const isLanguageSelected = (languageId) => {
        return selectedLanguages.some(l => l.id === languageId);
    }

    const toggleLanguage = (language) => {
        const newLanguages = [...selectedLanguages.filter(sl => sl.id !== language.id)];

        let result = newLanguages;

        if (!isLanguageSelected(language.id)) {
            result.push(language);
        }

        console.debug("Setting languages", language, newLanguages);
        setHasChanges(true);
        setSelectedLanguages(result);
    }

    const fetchPageData = async (token) => {
        try {
            setLoadingPage(true);
            const response = await fetch(`/api/pages/full/${pageId}`, {
                headers: {
                    'X-CSRF-TOKEN': token
                },
                credentials: 'include' // This ensures cookies are sent with the request
            });
            
            if (response.status === 401) {
                window.location.href = '/login'; // Redirect to login page
                return;
            }
            
            if (!response.ok) throw new Error('Failed to fetch page data');
            
            const pageData = await response.json();
            setPage(pageData);
            setTitle(pageData.title || '');
            setWebpage(pageData.webpage || '');
            setPhone(pageData.phone || '');
            setEmail(pageData.email || '');
            setLogo(pageData.logo || '');
            setContent(pageData.content ? JSON.parse(pageData.content) : {});
            setSelectedLanguages(pageData.languages || []);
            setLocations(pageData.locationInstances || []);
            setNewLocations(pageData.locationInstances || []);
            setSelectedServices(pageData.serviceInstances);
        } catch (error) {
            console.error('Error fetching page data:', error);
            setError(error.message);
        } finally {
            setLoadingPage(false);
        }
    };

    const fetchCategories = async (token) => {
        setLoadingCategories(true);
        const response = await fetch("/api/fullCategories", {
            headers: {
                'X-CSRF-TOKEN': token
            },
            credentials: 'include'
        });
            
        if (response.status === 401) {
            window.location.href = '/login'; // Redirect to login page
            return;
        }
        
        if (!response.ok) throw new Error('Failed to fetch page data');
        const categories = await response.json();

        setCategories(categories);
        setLoadingCategories(false);
    }

    const fetchLanguages = async (token) => {
        setLoadingLanguages(true);
        const response = await fetch("/api/languages", {
            headers: {
                'X-CSRF-TOKEN': token
            },
            credentials: 'include'
        });

        if (response.status === 401) {
            window.location.href = '/login';
            return;
        }

        if (!response.ok) throw new Error('Failed to fetch language data');
        const languages = await response.json();

        setLanguages(languages);
        setLoadingLanguages(false);
    }

    const initializeEditor = (initialContent) => {
        if (!containerRef.current) {
            console.error('Editor container not found');
            return;
        }

        if (!window.EditorJS) {
            console.warn('EditorJS not loaded yet, retrying...');
            setTimeout(() => initializeEditor(initialContent), 200);
            return;
        }

        const editorInstance = new window.EditorJS({
            holder: containerRef.current,
            inlineToolbar: true,
            placeholder: "Write here..",
            tools: {
              header: {
                class: window.Header,
                config: {
                  placeholder: 'Header',
                  defaultLevel: 2,
                  levels: [2, 3, 4],
        
                },
                toolbox: [
                  {
                    icon: '<svg> ... </svg>',
                    title: 'Heading 1',
                    data: {
                      level: 2,
                    },
                  },
                  {
                    icon: '<svg> ... </svg>',
                    title: 'Heading 2',
                    data: {
                      level: 3,
                    },
                  },
                  {
                    icon: '<svg> ... </svg>',
                    title: 'Heading 3',
                    data: {
                      level: 4,
                    },
                  }
                ],
        
                inlineToolbar: true
              },
              table: window.Table,
            //   quote: Quote,
              list: {
                class: window.EditorjsList,
                inlineToolbar: true
              },
              image: {
                class: window.ImageTool,
                inlineToolbar: ['link'],
                config: {
                  endpoints: {
                    byFile: '/image/upload'
                  },
                  additionalRequestData: {
                    'csrf-token': JSON.parse(document.body.getAttribute("hx-headers"))['X-CSRF-TOKEN']
                  },
                  features: {
                    border: true,
                    caption: 'optional',
                    stretch: true
                  }
                }
              },
              delimiter: window.Delimiter,
              // paragraph: Paragraph,
              embed: window.Embed
            },
            data: initialContent,
            onChange: async (api) => {
                const data = await api.saver.save();

                const dataWithoutTime = {...data, time: null};
                const contentWithoutTime = {...content, time: null};

                if (JSON.stringify(dataWithoutTime) !== JSON.stringify(contentWithoutTime)) {
                    console.debug("Page content changed", data)
                    setHasChanges(true);
                }

                setContent(data);
            },
          });
        editorRef.current = editorInstance;
    };

    useEffect(() => {
        if (!loadingPage && !loadingCategories) {
            initializeEditor(content);
        }
    }, [loadingPage, loadingCategories]);

    const handleSave = async () => {
        try {
            // Get the latest content from EditorJS
            const latestContent = await editorRef.current.save();
            setContent(latestContent);
            setHasChanges(false);

            // Prepare the page data
            const pageData = {
                title,
                content: latestContent,
                email,
                phone,
                logo,
                webpage,
                languageIds: selectedLanguages.map(l => l.id),
                locationInstances: newLocations && newLocations.map(loc => ({
                    id: loc.id,
                    locationId: loc.location.id,
                    address: loc.address,
                    latitude: loc.latitude,
                    longitude: loc.longitude
                })) || [],
                serviceInstances: serviceInstances || []
            };

            console.debug("Saving page", pageData);

            // Send the update request
            const response = await fetch(`/api/pages/${pageId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                credentials: 'include', // This ensures cookies are sent with the request
                body: JSON.stringify(pageData),
            });

            if (response.status === 401) {
                window.location.href = '/login'; // Redirect to login page
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to save page');
            } else {
                window.removeEventListener("beforeunload", onBeforeUnload);
                window.location.href = '/sidor/' + title.toLowerCase().replace(" ", "_") + '/' + pageId; 
            }

            // Redirect to the page view
            // window.location.href = `/${page.slug}/${pageId}`;
        } catch (error) {
            console.error('Error saving page:', error);
            setError(error.message);
        }
    };

    if (loadingPage || loadingCategories || loadingLanguages) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div className="alert alert-danger">Error: {error}</div>;
    }

    if (!page) {
        return <div className="alert alert-warning">Page not found</div>;
    }

    return (
        <div className="edit-page">
            <div className="form-group">
                <label htmlFor="title">Titel</label>
                <input
                    type="text"
                    id="title"
                    className="form-control"
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        console.debug("Title changed", e.target.value);
                        setHasChanges(true);
                    }}
                />
            </div>

            <div className="form-group">
                <label htmlFor="content">Sidinnehåll</label>
                <div ref={containerRef} />
            </div>


            <div className="form-group">
                <label>Logotyp</label>
                <LogoUploader currentImage={page.logo} onImageChange={(image) => setLogo(image)} />
            </div>

            <div className="form-group">
                <label htmlFor="webpage">Hemsida</label>
                <input
                    type="text"
                    id="webpage"
                    className="form-control"
                    value={webpage}
                    onChange={(e) => {
                        setWebpage(e.target.value);
                        console.debug("Webpage changed", e.target.value);
                        setHasChanges(true);
                    }}
                />
            </div>

            <div className="form-group">
                <label htmlFor="phone">Telefonnummer</label>
                <input
                    type="text"
                    id="phone"
                    className="form-control"
                    value={phone}
                    onChange={(e) => {
                        setPhone(e.target.value);
                        console.debug("Phone changed", e.target.value);
                        setHasChanges(true);
                    }}
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">Mailadress</label>
                <input
                    type="text"
                    id="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        console.debug("Email changed", e.target.value);
                        setHasChanges(true);
                    }}
                />
            </div>

            <div className="form-group">
                <label>Platser</label>
                <LocationManager 
                    initialLocationInstances={locations}
                    onLocationsChange={(loc) => {
                        console.debug("Locations changed", loc);
                        if (loc !== newLocations) {
                            setHasChanges(true);
                        }
                        setNewLocations(loc);

                    }}
                />
            </div>

            <div className="form-group">
                <label>Tjänster</label>
                <CategoryManager
                    categories={categories}
                    initialServiceInstances={page.serviceInstances}
                    onSelectionsChange={serviceInstances => {
                        console.debug("Service instances changed", serviceInstances);
                        setHasChanges(true);
                        setSelectedServices(serviceInstances)}
                    }
                />
            </div>

            <div className="form-group">
                <label>Språk</label>
                <div className="lang-picker">
                    {languages.map(language => {
                        const selected = isLanguageSelected(language.id);
                        return (
                            <button
                                key={language.id}
                                type="button"
                                className={`lang-picker__item ${selected ? 'lang-picker__item--active' : ''}`}
                                onClick={() => toggleLanguage(language)}
                            >
                                <i className={`fa-solid ${selected ? 'fa-circle-check' : 'fa-circle'}`}></i>
                                {language.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            <button className="btn btn-success btn-lg" onClick={() => {
                setHasChanges(false);
                handleSave();
            }}>
                <i className="fa-solid fa-floppy-disk"></i> Spara sida
            </button>
        </div>
    );
};

// Function to create an EditPage instance
window.createEditPage = (containerId, pageId) => {
    const container = document.getElementById(containerId);
    if (container) {
        const root = ReactDOM.createRoot(container);
        root.render(<EditPage pageId={pageId} />);
    }
};

export default EditPage; 